import React, { useState } from "react";
import {
  PDFViewer,
  Page,
  Text,
  View,
  Document,
  Image,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";
import { createTw } from "react-pdf-tailwind";
import { Request } from "../../hooks/Request";

import {
  calculateInsuredItemTotal,
  MIN_INSURANCE_PREMIUM_USD,
  normalizeInsuranceCurrency,
  toInsuranceNumber,
} from "../../utils/insuranceRules";

const tw = createTw({
  theme: {
    fontFamily: { sans: "Helvetica" },
    fontSize: { tiny: "7px", xxs: "8px", xs: "10px", sm: "12px" },
    extend: {
      colors: {
        blueHeader: "#9bc2e6",
      },
    },
  },
});

Font.registerHyphenationCallback((word) => [word]);

const styles = StyleSheet.create({
  pageContent: {
    padding: 30,
    fontFamily: "Helvetica",
    fontSize: 10,
    lineHeight: 1.2,
  },
  bold: { fontWeight: "bold" },
  underlineInput: {
    borderBottom: "1pt solid black",
    paddingBottom: 2,
    flex: 1,
    minHeight: 14,
  },
  tableCell: {
    borderRight: "1pt solid black",
    display: "flex",
    justifyContent: "center",
    paddingHorizontal: 4,
    paddingVertical: 1,
  },
});

const formatoFechaLarga = (fechaStr) => {
  if (!fechaStr) return "";
  const meses = [
    "enero",
    "febrero",
    "marzo",
    "abril",
    "mayo",
    "junio",
    "julio",
    "agosto",
    "septiembre",
    "octubre",
    "noviembre",
    "diciembre",
  ];
  const partes = fechaStr.split("T")[0].split("-");
  if (partes.length !== 3) return fechaStr;
  return `${partes[2]} de ${meses[parseInt(partes[1]) - 1]} de ${partes[0]}`;
};

const formatoFechaCorta = (fechaStr) => {
  if (!fechaStr) return "";
  const partes = fechaStr.split("T")[0].split("-");
  if (partes.length !== 3) return fechaStr;
  return `${partes[2]}/${partes[1]}/${partes[0]}`;
};

const formatMoney = (value) =>
  Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const parseItemExtra = (item) => {
  try {
    return typeof item?.description2 === "string"
      ? JSON.parse(item.description2)
      : item?.description2 || {};
  } catch {
    return {};
  }
};

export const InsuranceApplicationPDF = (props) => {
  const { procedureId } = props;
  const [agrupar, setAgrupar] = useState(false);

  const listFields = [
    "polizaNro",
    "fechaDeEmbarque",
    "itemsOrdenDeCompra",
    "facturaOProformaODoc",
    "fechaFactura",
    "travesíaDesde",
    "travesíaHasta",
    "medioTransporte",
    "cobertura",
    "tasa",
    "tipoDeCambioSeguroEurUsd",
    "moneda",
    "AplicaciónNo",
    "cantidadDeLaPolizaAgrupada",
    "unidadDeLaPolizaAgrupada",
    "descripcionDeLaPoliza",
  ];

  const queryParams = listFields
    .map((field) => `fieldnames=${field}`)
    .join("&");

  const { data, loading, error } = Request({
    urlApi: `/Procedure/${procedureId}/dataValues?${queryParams}`,
  });

  const formData = {
    ...(data || {}),
    travesíaHasta: data?.travesíaHasta || "COCHABAMBA, BOLIVIA",
    cobertura: data?.cobertura || "TODO RIESGO",
    polizaNro: data?.polizaNro || "NAN",
  };

  const itemsRaw = formData.itemsOrdenDeCompraSubDatas || [];
  const firstItemExtra = parseItemExtra(itemsRaw[0]);

  const purchaseOrderCurrency = normalizeInsuranceCurrency(
    formData.moneda || firstItemExtra.purchaseOrderCurrency || "USD",
  );

  const eurUsdRate = toInsuranceNumber(formData.tipoDeCambioSeguroEurUsd);
  const requiresEurConversion = purchaseOrderCurrency === "EUR";

  if (requiresEurConversion && eurUsdRate <= 0) {
    return (
      <div className="p-6 text-center text-red-600 font-semibold">
        Para generar la aplicación de seguro en USD, ingrese un tipo de cambio
        EUR/USD mayor a 0.
      </div>
    );
  }

  let totalCostoGlobal = 0;
  let totalCantidad = 0;
  let insuranceRuleUsed = null;

  // 1. Procesar items normalmente (sin la lógica de duplicados rota)
  const itemsProcesados = itemsRaw.map((item) => {
    const extra = parseItemExtra(item);

    const { rule, insuredTotalUsd } = calculateInsuredItemTotal(extra, {
      currency: purchaseOrderCurrency,
      exchangeRateEurUsd: eurUsdRate,
    });

    if (!insuranceRuleUsed) {
      insuranceRuleUsed = rule;
    }

    totalCostoGlobal += insuredTotalUsd;
    totalCantidad += extra.amount;

    return {
      cantidad: extra.amount || "-",
      unidad: extra.unit || "-",
      descripcion: extra.desc || item.label || "",
      costoTotal: insuredTotalUsd,
    };
  });

  // 2. Agrupar por descripción (para el modo "Agrupar items")
  const itemsAgrupados = Object.values(
    itemsProcesados.reduce((acc, item) => {
      const key = (item.descripcion || "SIN DESCRIPCIÓN").toUpperCase();

      if (!acc[key]) {
        acc[key] = {
          cantidad: 0, // acá cuenta cuántos items se agruparon
          unidad: "-",
          descripcion: item.descripcion,
          costoTotal: 0,
        };
      }

      acc[key].cantidad += 1;
      acc[key].costoTotal += item.costoTotal;

      return acc;
    }, {}),
  );

  const tasaSeguro =
    toInsuranceNumber(formData.tasa) || insuranceRuleUsed?.ratePercent || 0;

  const primaCalculada = totalCostoGlobal * (tasaSeguro / 100);

  const primaTotal =
    totalCostoGlobal > 0 && tasaSeguro > 0
      ? Math.max(primaCalculada, MIN_INSURANCE_PREMIUM_USD)
      : 0;

  const poliza = formData.polizaNro || "no encontrado";
  const compania = formData?.company?.razonSocial || "no encontrado";
  const aplicacionNo = formData.AplicaciónNo || "-";
  const fechaHoy = formData.fechaDeEmbarque
    ? formatoFechaLarga(formData.fechaDeEmbarque)
    : formatoFechaLarga(new Date().toISOString());

  const itemsAMostrar = agrupar ? itemsAgrupados : itemsProcesados;

  const emptyRowsCount = Math.max(0, 15 - itemsAMostrar.length);
  const emptyRows = Array.from({ length: emptyRowsCount });

  const MyDoc = (
    <Document>
      <Page size="LETTER" style={styles.pageContent}>
        <View style={tw("flex flex-row justify-between items-center mb-6")}>
          <View style={tw("w-[40%] flex items-start justify-center h-[40px]")}>
            <Image
              src="/images/logo-credinform.jpg"
              style={{ width: 160, height: 40, objectFit: "contain" }}
            />
          </View>
          <View style={tw("w-[40%] flex items-end justify-center h-[40px]")}>
            <Image
              src="/images/logo-kieffer.png"
              style={{ width: 160, height: 40, objectFit: "contain" }}
            />
          </View>
        </View>

        <View style={tw("flex flex-row justify-between mb-4")}>
          <View style={tw("w-[55%] flex flex-col")}>
            <View style={tw("flex flex-row items-end mb-2")}>
              <Text style={tw("w-[25%] font-bold text-right pr-2")}>
                Póliza
              </Text>
              <View style={styles.underlineInput}>
                <Text>CM - CBR0595503</Text>
              </View>
            </View>
            <View style={tw("flex flex-row items-end mb-2")}>
              <Text style={tw("w-[25%] font-bold text-right pr-2")}>
                Asegurado
              </Text>
              <View style={styles.underlineInput}>
                <Text>PLASTIFORTE SRL</Text>
              </View>
            </View>
            <View style={tw("flex flex-row items-end")}>
              <Text style={tw("w-[25%] font-bold text-right pr-2")}>
                Broker
              </Text>
              <View style={styles.underlineInput}>
                <Text>KIEFFER Y ASOCIADOS S.A.</Text>
              </View>
            </View>
          </View>

          <View style={tw("w-[40%] flex flex-col")}>
            <View style={tw("flex flex-row items-end justify-end mb-6")}>
              <Text style={tw("font-bold mr-2")}>Cochabamba:</Text>
              <Text>{fechaHoy}</Text>
            </View>
            <View style={tw("flex flex-row items-end justify-end")}>
              <Text style={tw("font-bold mr-2")}>Aplicación No</Text>
              <View
                style={[styles.underlineInput, tw("max-w-[40px] items-center")]}
              >
                <Text>{aplicacionNo}</Text>
              </View>
            </View>
          </View>
        </View>

        <View style={tw("flex flex-col w-full mb-1")}>
          <View
            style={tw(
              "flex flex-row bg-[#99ccff] border-t border-l border-r border-black",
            )}
          >
            <View
              style={[{ width: agrupar ? "15%" : "12%" }, styles.tableCell]}
            >
              <Text style={tw("font-bold text-xs text-center")}>
                {agrupar ? "NRO DE ITEMS" : "CANTIDAD"}
              </Text>
            </View>

            {!agrupar && (
              <View style={[{ width: "12%" }, styles.tableCell]}>
                <Text style={tw("font-bold text-xs text-center")}>UNIDAD</Text>
              </View>
            )}

            <View
              style={[{ width: agrupar ? "65%" : "56%" }, styles.tableCell]}
            >
              <Text style={tw("font-bold text-xs")}>DESCRIPCIÓN DEL ITEM</Text>
            </View>
            <View
              style={[
                { width: "20%", borderRight: "none", alignItems: "center" },
                styles.tableCell,
              ]}
            >
              <Text style={tw("font-bold text-xs")}>COSTO TOTAL</Text>
              <Text style={tw("font-bold text-xs")}>(USD)</Text>
            </View>
          </View>

          {itemsAMostrar.map((item, idx) => (
            <View
              key={idx}
              style={tw(
                "flex flex-row border-t border-l border-r border-black min-h-[16px]",
              )}
            >
              <View
                style={[
                  { width: agrupar ? "15%" : "12%", alignItems: "center" },
                  styles.tableCell,
                ]}
              >
                <Text style={tw("text-xs")}>
                  {item.cantidad !== "-"
                    ? formatMoney(item.cantidad)
                    : item.cantidad}
                </Text>
              </View>

              {!agrupar && (
                <View
                  style={[
                    { width: "12%", alignItems: "center" },
                    styles.tableCell,
                  ]}
                >
                  <Text style={tw("text-xs")}>
                    {item.unidad?.toUpperCase() || "-"}
                  </Text>
                </View>
              )}
              <View
                style={[
                  {
                    width: agrupar ? "65%" : "56%",
                    alignItems: "flex-start",
                  },
                  styles.tableCell,
                ]}
              >
                <Text style={tw("text-xs")}>
                  {item.descripcion?.toUpperCase() || "-"}
                </Text>
              </View>
              <View
                style={[
                  { width: "20%", borderRight: "none", alignItems: "flex-end" },
                  styles.tableCell,
                ]}
              >
                <Text style={tw("text-xs")}>
                  USD {formatMoney(item.costoTotal)}
                </Text>
              </View>
            </View>
          ))}

          {emptyRows.map((_, idx) => (
            <View
              key={`empty-${idx}`}
              style={tw(
                "flex flex-row border-t border-l border-r border-black h-[14px]",
              )}
            >
              <View
                style={[{ width: agrupar ? "15%" : "12%" }, styles.tableCell]}
              />

              {!agrupar && (
                <View style={[{ width: "12%" }, styles.tableCell]} />
              )}

              <View
                style={[
                  {
                    width: agrupar ? "65%" : "56%",
                  },
                  styles.tableCell,
                ]}
              />

              <View
                style={[
                  {
                    width: "20%",
                    borderRight: "none",
                  },
                  styles.tableCell,
                ]}
              />
            </View>
          ))}
          <View style={tw("border-t border-black w-full")}></View>
        </View>

        <View style={tw("flex flex-col items-end w-full mb-6")}>
          <View style={tw("flex flex-row justify-end w-full")}>
            <Text style={tw("font-bold mr-2 mt-1")}>Totales USD</Text>
            <View
              style={tw(
                "w-[20%] border border-black items-end justify-center px-4 py-1",
              )}
            >
              <Text>USD {formatMoney(totalCostoGlobal)}</Text>
            </View>
          </View>

          {/* {requiresEurConversion && (
            <Text style={tw("text-[7px] mt-1 italic")}>
              Valores convertidos a USD desde EUR. Tipo de cambio aplicado: 1
              EUR = {formatMoney(eurUsdRate)} USD.
            </Text>
          )} */}
        </View>

        <View style={tw("flex flex-col w-[85%]")}>
          <View style={tw("flex flex-row items-end mb-2")}>
            <Text style={tw("w-[30%] font-bold text-right pr-2")}>
              Cliente:
            </Text>
            <View style={[styles.underlineInput, tw("max-w-[40%]")]}>
              <Text>{(compania || "SIN DATOS").toUpperCase()}</Text>
            </View>
          </View>

          <View style={tw("flex flex-row items-end mb-2")}>
            <Text style={tw("w-[30%] font-bold text-right pr-2")}>
              Factura o Proforma:
            </Text>
            <View style={[styles.underlineInput, tw("max-w-[40%]")]}>
              <Text>
                {(formData.facturaOProformaODoc
                  ? `${formData.facturaOProformaODoc}${formData.fechaFactura ? ` DE FECHA ${formatoFechaCorta(formData.fechaFactura)}` : ""}`
                  : "SIN DATOS"
                ).toUpperCase()}
              </Text>
            </View>
          </View>

          <View style={tw("flex flex-row items-end mb-2")}>
            <Text style={tw("w-[30%] font-bold text-right pr-2")}>
              Travesia: Desde
            </Text>
            <View style={[styles.underlineInput, tw("max-w-[40%]")]}>
              <Text>
                {(formData.travesíaDesde || "SIN DATOS").toUpperCase()}
              </Text>
            </View>
          </View>

          <View style={tw("flex flex-row items-end mb-2")}>
            <Text style={tw("w-[30%] font-bold text-right pr-2")}>Hasta</Text>
            <View style={[styles.underlineInput, tw("max-w-[40%]")]}>
              <Text>
                {" "}
                {(formData.travesíaHasta || "SIN DATOS").toUpperCase()}
              </Text>
            </View>
          </View>

          <View style={tw("flex flex-row items-end mb-2")}>
            <Text style={tw("w-[30%] font-bold text-right pr-2")}>
              Fecha de Embarque
            </Text>
            <View style={[styles.underlineInput, tw("max-w-[40%]")]}>
              <Text>
                {(
                  formatoFechaCorta(formData.fechaDeEmbarque) || "SIN DATOS"
                ).toUpperCase()}
              </Text>
            </View>
          </View>

          <View style={tw("flex flex-row items-end mb-2")}>
            <Text style={tw("w-[30%] font-bold text-right pr-2")}>
              Medio Transporte
            </Text>
            <View style={[styles.underlineInput, tw("max-w-[40%]")]}>
              <Text>
                {(formData.medioTransporte || "SIN DATOS").toUpperCase()}
              </Text>
            </View>
          </View>

          <View style={tw("flex flex-row items-end mb-2")}>
            <Text style={tw("w-[30%] font-bold text-right pr-2")}>
              Cobertura
            </Text>
            <View style={[styles.underlineInput, tw("max-w-[40%]")]}>
              <Text>{(formData.cobertura || "SIN DATOS").toUpperCase()}</Text>
            </View>
          </View>

          <View style={tw("flex flex-row items-end mb-2")}>
            <Text style={tw("w-[30%] font-bold text-right pr-2")}>Tasa</Text>
            <View style={[styles.underlineInput, tw("max-w-[20%]")]}>
              <Text>
                {(tasaSeguro ? `${tasaSeguro} %` : "SIN DATOS").toUpperCase()}
              </Text>
            </View>
          </View>

          <View style={tw("flex flex-row items-end mb-2")}>
            <Text style={tw("w-[30%] font-bold text-right pr-2")}>
              Prima Total
            </Text>
            <View style={[styles.underlineInput, tw("max-w-[20%]")]}>
              <Text>
                {primaTotal > 0
                  ? `USD ${formatMoney(primaTotal)}`
                  : "SIN DATOS"}
              </Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );

  return (
    <div className="flex flex-col gap-2 w-[80vw] h-[85vh]">
      <div className="flex gap-2 items-center p-2 bg-gray-100 rounded-md border border-gray-200 w-fit ml-4">
        <input
          id="agrupar"
          type="checkbox"
          checked={agrupar}
          onChange={(e) => setAgrupar(e.target.checked)}
          className="w-4 h-4 cursor-pointer"
        />
        <label htmlFor="agrupar" className="text-sm font-bold cursor-pointer">
          Agrupar items
        </label>
      </div>
      <PDFViewer
        style={{
          width: "100%",
          height: window.innerHeight - 100,
        }}
      >
        {MyDoc}
      </PDFViewer>
    </div>
  );
};
